import { ethers } from "ethers";

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined';

// Browser-compatible cache using localStorage or in-memory
class EventCache {
  private cache: Map<string, EventCacheFile> = new Map();
  private cacheDir: string = '';

  constructor() {
    if (!isBrowser) {
      // Node.js environment - use fs
      try {
        const fs = require('fs');
        const path = require('path');
        this.cacheDir = path.join(__dirname, "..", ".cache");
        if (!fs.existsSync(this.cacheDir)) {
          fs.mkdirSync(this.cacheDir, { recursive: true });
        }
      } catch (e) {
        console.warn('Failed to initialize file cache, falling back to memory:', e);
      }
    }
  }

  private getCacheKey(contractAddress: string, eventName: string): string {
    return `${contractAddress.toLowerCase()}-${eventName}`;
  }

  loadCache(contractAddress: string, eventName: string): EventCacheFile | null {
    const key = this.getCacheKey(contractAddress, eventName);
    
    if (isBrowser) {
      // Browser environment - use localStorage
      try {
        const data = localStorage.getItem(key);
        if (data) {
          return JSON.parse(data);
        }
      } catch (e) {
        console.warn('Failed to load cache from localStorage:', e);
      }
      return null;
    } else {
      // Node.js environment - use fs
      try {
        const fs = require('fs');
        const path = require('path');
        const filePath = path.join(this.cacheDir, `${key}.json`);
        if (fs.existsSync(filePath)) {
          return JSON.parse(fs.readFileSync(filePath, 'utf-8'));
        }
      } catch (e) {
        console.warn('Failed to load cache from file:', e);
      }
      return null;
    }
  }

  saveCache(contractAddress: string, eventName: string, cache: EventCacheFile): void {
    const key = this.getCacheKey(contractAddress, eventName);
    
    if (isBrowser) {
      // Browser environment - use localStorage
      try {
        localStorage.setItem(key, JSON.stringify(cache));
      } catch (e) {
        console.warn('Failed to save cache to localStorage:', e);
      }
    } else {
      // Node.js environment - use fs
      try {
        const fs = require('fs');
        const path = require('path');
        const filePath = path.join(this.cacheDir, `${key}.json`);
        fs.writeFileSync(filePath, JSON.stringify(cache, null, 2));
      } catch (e) {
        console.warn('Failed to save cache to file:', e);
      }
    }
  }

  clearCache(contractAddress: string, eventName: string): void {
    const key = this.getCacheKey(contractAddress, eventName);
    
    if (isBrowser) {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        console.warn('Failed to clear cache from localStorage:', e);
      }
    } else {
      try {
        const fs = require('fs');
        const path = require('path');
        const filePath = path.join(this.cacheDir, `${key}.json`);
        if (fs.existsSync(filePath)) {
          fs.unlinkSync(filePath);
        }
      } catch (e) {
        console.warn('Failed to clear cache file:', e);
      }
    }
  }
}

const CHUNK_SIZE = 9500; // stays under common RPC eth_getLogs range caps

interface CachedPlantEvent {
  plant: string;
  timestamp: number;
  blockNumber: number;
  logIndex: number;
}

interface EventCacheFile {
  lastScannedBlock: number;
  events: CachedPlantEvent[];
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Global cache instance
const eventCache = new EventCache();

// Helper to get provider from contract (works with both v5 and v6)
function getProviderFromContract(contract: ethers.Contract): ethers.Provider | null {
  try {
    // v6: contract.runner might be a provider or signer
    if (contract.runner) {
      // If it's a signer, get the provider from it
      if (typeof contract.runner === 'object' && 'provider' in contract.runner && contract.runner.provider) {
        return contract.runner.provider as ethers.Provider;
      }
      // If it's already a provider
      if (typeof contract.runner === 'object' && 'getBlockNumber' in contract.runner) {
        return contract.runner as ethers.Provider;
      }
    }
    
    // v5: contract.provider
    if (contract.provider && typeof contract.provider === 'object' && 'getBlockNumber' in contract.provider) {
      return contract.provider as ethers.Provider;
    }
    
    return null;
  } catch (error) {
    console.warn('Failed to get provider from contract:', error);
    return null;
  }
}

// Helper to get contract address (works with both v5 and v6)
async function getContractAddress(contract: ethers.Contract): Promise<string | null> {
  try {
    // v6: async getAddress()
    if (typeof contract.getAddress === 'function') {
      return await contract.getAddress();
    }
    // v5: address property
    if (typeof contract.address === 'string') {
      return contract.address;
    }
    return null;
  } catch (error) {
    console.warn('Failed to get contract address:', error);
    return null;
  }
}

// Binary search for the earliest block at which the contract already has code,
// so scans start at deployment instead of block 0.
async function findDeploymentBlock(
  provider: ethers.Provider,
  address: string,
  latestBlock: number,
): Promise<number> {
  let lo = 0;
  let hi = latestBlock;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    const code = await provider.getCode(address, mid);
    if (code && code !== "0x") {
      hi = mid;
    } else {
      lo = mid + 1;
    }
  }
  return lo;
}

// Queries a block range for a filter, bisecting the range if the RPC provider
// rejects it as too large (providers vary: 2k / 10k / 50k eth_getLogs caps),
// and retrying transient failures with backoff.
async function queryRangeAdaptive(
  contract: ethers.Contract,
  eventName: string,
  fromBlock: number,
  toBlock: number,
  retriesLeft = 4,
): Promise<ethers.EventLog[]> {
  try {
    // In v6, queryFilter accepts event name as string and returns (EventLog | Log)[]
    const result = await contract.queryFilter(eventName, fromBlock, toBlock);
    
    // Cast to EventLog[] since we know our event is typed
    return result as ethers.EventLog[];
  } catch (err: any) {
    const message = String(err?.error?.message || err?.message || "");
    const isRangeError = /exceeds limit|range|too large|block range/i.test(
      message,
    );
    if (isRangeError && toBlock > fromBlock) {
      const mid = fromBlock + Math.floor((toBlock - fromBlock) / 2);
      const [left, right] = await Promise.all([
        queryRangeAdaptive(contract, eventName, fromBlock, mid, retriesLeft),
        queryRangeAdaptive(contract, eventName, mid + 1, toBlock, retriesLeft),
      ]);
      return [...left, ...right];
    }
    if (retriesLeft > 0) {
      await sleep(500);
      return queryRangeAdaptive(
        contract,
        eventName,
        fromBlock,
        toBlock,
        retriesLeft - 1,
      );
    }
    throw err;
  }
}

// Process a single chunk of events
async function processChunk(
  contract: ethers.Contract,
  eventName: string,
  from: number,
  to: number,
): Promise<CachedPlantEvent[]> {
  const events = await queryRangeAdaptive(contract, eventName, from, to);
  
  return events
    .map((ev: any) => ({
      plant: ev.args?.plant as string,
      timestamp: ev.args?.timestamp ? Number(ev.args.timestamp.toString()) : NaN,
      blockNumber: ev.blockNumber as number,
      logIndex: ev.logIndex as number,
    }))
    .filter((e) => e.plant && !Number.isNaN(e.timestamp));
}

// Save cache with error handling
function saveCacheWithRetry(
  contractAddress: string,
  eventName: string,
  cache: EventCacheFile,
  maxRetries = 3
): void {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      eventCache.saveCache(contractAddress, eventName, cache);
      return;
    } catch (error) {
      if (attempt === maxRetries) {
        console.error(`Failed to save cache after ${maxRetries} attempts:`, error);
        throw error;
      }
      // Wait before retry with exponential backoff
      sleep(100 * attempt);
    }
  }
}

/**
 * Fetches every PlantRecorded event for a contract (all plants), backed by a
 * per-contract file cache keyed on the last block scanned. The full
 * historical scan (from contract deployment) only ever runs once; later
 * calls only fetch blocks newer than what's cached.
 */
export async function getPlantRecordedEvents(
  contract: ethers.Contract,
): Promise<CachedPlantEvent[]> {
  try {
    // Get provider from the contract using the helper function
    const provider = getProviderFromContract(contract);
    if (!provider) {
      console.error('No provider available for contract');
      return [];
    }
    
    const latestBlock = await provider.getBlockNumber();
    
    // Get contract address using helper
    const contractAddress = await getContractAddress(contract);
    if (!contractAddress) {
      console.error('No contract address available');
      return [];
    }
    
    const eventName = "PlantRecorded";

    let cache = eventCache.loadCache(contractAddress, eventName);
    let fromBlock: number;

    if (cache) {
      fromBlock = cache.lastScannedBlock + 1;
    } else {
      fromBlock = await findDeploymentBlock(
        provider,
        contractAddress,
        latestBlock,
      );
      cache = { lastScannedBlock: fromBlock - 1, events: [] };
    }

    if (fromBlock <= latestBlock) {
      // Build all chunk requests for parallel execution
      const chunkPromises: Promise<CachedPlantEvent[]>[] = [];
      const chunks: { from: number; to: number }[] = [];
      
      for (let from = fromBlock; from <= latestBlock; from += CHUNK_SIZE) {
        const to = Math.min(from + CHUNK_SIZE - 1, latestBlock);
        chunks.push({ from, to });
        chunkPromises.push(processChunk(contract, eventName, from, to));
      }

      // Execute all chunks in parallel with settled handling
      const chunkResults = await Promise.allSettled(chunkPromises);
      
      // Process results and save incrementally
      let failedChunks = 0;
      let processedEvents = 0;
      
      for (let i = 0; i < chunkResults.length; i++) {
        const result = chunkResults[i];
        const { from, to } = chunks[i];
        
        if (result.status === 'fulfilled') {
          const mappedEvents = result.value;
          cache.events.push(...mappedEvents);
          processedEvents += mappedEvents.length;
          cache.lastScannedBlock = to;
          
          // Save after each successful chunk
          try {
            saveCacheWithRetry(contractAddress, eventName, cache);
          } catch (error) {
            console.error(`Failed to save cache after chunk ${i + 1}:`, error);
            // Continue processing other chunks
          }
        } else {
          failedChunks++;
          console.warn(
            `Failed to process chunk from ${from} to ${to}:`,
            result.reason?.message || result.reason
          );
        }
      }

      if (failedChunks > 0) {
        console.warn(
          `getPlantRecordedEvents: ${failedChunks} chunk(s) failed to process`
        );
      }
      
      if (processedEvents > 0) {
        console.log(
          `getPlantRecordedEvents: Processed ${processedEvents} events from ${chunks.length} chunks`
        );
      }
    }

    return cache.events;
  } catch (error) {
    console.error('Error in getPlantRecordedEvents:', error);
    // Return cached events if available, otherwise empty array
    return [];
  }
}

/**
 * Sorted (block, logIndex ascending) PlantRecorded timestamps for one plant,
 * drawn from the shared cache. Index-aligned with that plant's
 * getPlantEmissionData() array, same as the emission-record insertion order.
 */
export async function getPlantRecordedTimestamps(
  contract: ethers.Contract,
  plantAddress: string,
): Promise<number[]> {
  const allEvents = await getPlantRecordedEvents(contract);
  return allEvents
    .filter((e) => e.plant.toLowerCase() === plantAddress.toLowerCase())
    .sort((a, b) => a.blockNumber - b.blockNumber || a.logIndex - b.logIndex)
    .map((e) => e.timestamp);
}

/**
 * Optimized version to get timestamps for multiple plants at once.
 * Uses the shared cache and groups events by plant.
 */
export async function getMultiplePlantRecordedTimestamps(
  contract: ethers.Contract,
  plantAddresses: string[],
): Promise<Record<string, number[]>> {
  const allEvents = await getPlantRecordedEvents(contract);
  
  // Group events by plant address
  const eventsByPlant = new Map<string, CachedPlantEvent[]>();
  
  for (const event of allEvents) {
    const lowerAddress = event.plant.toLowerCase();
    if (!eventsByPlant.has(lowerAddress)) {
      eventsByPlant.set(lowerAddress, []);
    }
    eventsByPlant.get(lowerAddress)!.push(event);
  }
  
  // Build result for requested plants
  const result: Record<string, number[]> = {};
  
  for (const address of plantAddresses) {
    const lowerAddress = address.toLowerCase();
    const events = eventsByPlant.get(lowerAddress) || [];
    
    result[address] = events
      .sort((a, b) => a.blockNumber - b.blockNumber || a.logIndex - b.logIndex)
      .map((e) => e.timestamp);
  }
  
  return result;
}

// Optional: Clear cache for a specific contract
export function clearPlantRecordedCache(contract: ethers.Contract): void {
  getContractAddress(contract).then((address) => {
    if (address) {
      eventCache.clearCache(address, "PlantRecorded");
    }
  }).catch((error) => {
    console.warn('Failed to clear cache:', error);
  });
}